import React, { useEffect } from "react";
import {
  ICreateTaskFormProps,
  StatusChangeEvent,
  TaskStatus,
} from "./definitions";
import styles from "app/style.module.scss";
import { Task, createTaskSchema } from "models/schemas";
import { FormProvider, get, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "app/store";
import {
  Box,
  Button,
  ColumnLayout,
  Form,
  FormField,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledInput,
  ControlledSelect,
  ControlledTextarea,
} from "@amzn/react-hook-form-polaris";
import { TaskTypeEnum } from "components/task-group/definitions";
import { toDateString, toUnixEpochTime } from "utils/date-format";
import { useCreateTaskMutation } from "components/create-task-form/endpoints";
import { useNotifications } from "features/notifications";
import { addTasksToList } from "features/audits/slice";
import { useTeamNameSelector, useUserNameSelector } from "./hooks";
import { handleStatusChange } from "./taskStatusUtils";
import { useGaiaObjectUiContent } from "features/ui-content";

const CreateTaskForm: React.FC<ICreateTaskFormProps> = (
  props: ICreateTaskFormProps
) => {
  const formMethods = useForm({
    mode: "onBlur",
    resolver: zodResolver(createTaskSchema),
  });
  const [disableAdd, setDisableAdd] = React.useState(false);
  const [subInquiryTypeOptions, setSubInquiryTypeOptions] = React.useState([]);
  const [previousStatus, setPreviousStatus] = React.useState("");
  const uiContent: any = useGaiaObjectUiContent(props.parentCategory);
  const { teamName } = useTeamNameSelector();
  const [createTask] = useCreateTaskMutation();
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const taskName = formMethods.watch("taskName");
  const { userName } = useUserNameSelector();

  const handleStatusChangeWrapper = (e: StatusChangeEvent) => {
    const newStatus = e.detail.selectedOption.value;
    const updatedStatus = handleStatusChange(
      newStatus,
      previousStatus,
      formMethods
    );
    setPreviousStatus(updatedStatus);
  };

  const onValid = (data: any, e: any) => {
    setDisableAdd(true);
    onCreateTask(data);
  };
  const oninvalid = (data: any, e: any) => {};

  const onCreateTask = async (task: any) => {
    try {
      task.taskType = TaskTypeEnum.TASK;
      task.parentTaskId = props.parentTaskId;
      task.teamName = teamName;
      task.plannedStartDate = toUnixEpochTime(task.plannedStartDateString);
      task.actualStartDate = toUnixEpochTime(task.actualStartDateString);
      task.dueDate = toUnixEpochTime(task.dueDateString);
      task.actualCompletionDate = toUnixEpochTime(
        task.actualCompletionDateString
      );
      task.status = task.status ?? TaskStatus.NotStarted;
      task.milestone = task.milestone;
      task.effort = task.effort ?? 0;

      if (props.onCreate) {
        props.onCreate(task);
        setDisableAdd(false);
        formMethods.reset();
        props.setVisible(false);
        return;
      }

      const response = await createTask(task).unwrap();

      const createdTask: Task = {
        ...task,
        taskId: response.taskId,
        plannedStartDateString: task.plannedStartDate
          ? toDateString(task.plannedStartDate)
          : "",
        dueDateString: task.dueDate ? toDateString(task.dueDate) : "",
        actualStartDateString: task.actualStartDate
          ? toDateString(task.actualStartDate)
          : "",
        actualCompletionDateString: task.actualCompletionDate
          ? toDateString(task.actualCompletionDate)
          : "",
      };
      dispatch(addTasksToList([createdTask]));

      addNotification({
        type: "success",
        header: "Create task successfully",
        dismissible: true,
        timeout: 10000,
      });
      setDisableAdd(false);
      formMethods.reset();
      props.setVisible(false);
    } catch (e) {
      addNotification({
        type: "error",
        header: "Failed to add the task",
        dismissible: true,
        timeout: 10000,
      });
      setDisableAdd(false);
    }
  };

  const onCancelAdd = async () => {
    formMethods.reset();
    props.setVisible(false);
  };

  useEffect(() => {
    if (props.resetForm) {
      formMethods.reset();
    }
  }, [formMethods, props.resetForm]);

  const handleTypeChange = (value: string) => {
    setSubInquiryTypeOptions(
      uiContent.subInquiryTypeOptions.find(
        (e: { label: string }) => e.label === value
      )?.options || []
    );
  };

  return (
    <div className={styles.createTaskForm} data-testid="create-task-form">
      <FormProvider {...formMethods}>
        <form
          role="form"
          onSubmit={formMethods.handleSubmit(onValid, oninvalid)}
        >
          <Form>
            <ColumnLayout columns={1}>
              <FormField
                label="Name"
                errorText={
                  (taskName &&
                    taskName.length > 200 &&
                    "Name exceeded character limit.") ||
                  get(formMethods.formState?.errors, `taskName.message`)
                }
                constraintText={
                  taskName && <div>Character count: {taskName.length}/200</div>
                }
              >
                <ControlledInput
                  name="taskName"
                  control={formMethods.control}
                  placeholder="Name"
                />
              </FormField>
              <FormField
                label="Owner"
                errorText={get(formMethods.formState?.errors, `owners.message`)}
              >
                <ControlledSelect
                  name="owners"
                  control={formMethods.control}
                  options={uiContent.teamAndCurResourceGuestOwnerOptions}
                  placeholder="Choose an option"
                  filteringType="auto"
                  defaultValue={userName}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Type - <i> optional</i>
                  </Box>
                }
              >
                <ControlledSelect
                  name="inquiryType"
                  control={formMethods.control}
                  options={uiContent.inquiryTypeOptions}
                  placeholder="Choose an option"
                  filteringType="auto"
                  onChange={(e) => {
                    if (e.detail.selectedOption.value === "\u00A0") {
                      formMethods.setValue("inquiryType", null);
                    } else {
                      handleTypeChange(e.detail.selectedOption.value!);
                    }
                  }}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Subtype - <i> optional</i>
                  </Box>
                }
              >
                <ControlledSelect
                  name="subInquiryType"
                  control={formMethods.control}
                  options={subInquiryTypeOptions}
                  placeholder="Choose an option"
                  filteringType="auto"
                />
              </FormField>
              <FormField label="Status">
                <ControlledSelect
                  name="status"
                  options={uiContent.taskStatusOptions}
                  control={formMethods.control}
                  placeholder="Choose an option"
                  filteringType="auto"
                  onChange={handleStatusChangeWrapper}
                  defaultValue={TaskStatus.NotStarted}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Planned start - <i> optional</i>
                  </Box>
                }
                errorText={get(
                  formMethods.formState?.errors,
                  `plannedStartDateString.message`
                )}
              >
                <ControlledDatePicker
                  name="plannedStartDateString"
                  control={formMethods.control}
                  placeholder="YYYY/MM/DD"
                  className={styles.dateCustomPicker}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Actual start - <i> optional</i>
                  </Box>
                }
                errorText={get(
                  formMethods.formState?.errors,
                  `actualStartDateString.message`
                )}
              >
                <ControlledDatePicker
                  name="actualStartDateString"
                  control={formMethods.control}
                  placeholder="YYYY/MM/DD"
                  className={styles.dateCustomPicker}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Due date - <i> optional</i>
                  </Box>
                }
                errorText={get(
                  formMethods.formState?.errors,
                  `dueDateString.message`
                )}
              >
                <ControlledDatePicker
                  name="dueDateString"
                  control={formMethods.control}
                  placeholder="YYYY/MM/DD"
                  className={styles.dateCustomPicker}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Completion date - <i> optional</i>
                  </Box>
                }
                errorText={get(
                  formMethods.formState?.errors,
                  `actualCompletionDateString.message`
                )}
              >
                <ControlledDatePicker
                  name="actualCompletionDateString"
                  control={formMethods.control}
                  placeholder="YYYY/MM/DD"
                  className={styles.dateCustomPicker}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Complexity - <i> optional</i>
                  </Box>
                }
              >
                <ControlledSelect
                  name="complexity"
                  control={formMethods.control}
                  options={uiContent.complexityOptions}
                  placeholder="Choose an option"
                  filteringType="auto"
                  onChange={(e) => {
                    if (e.detail.selectedOption.value === "\u00A0") {
                      formMethods.setValue("complexity", null);
                    }
                  }}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Priority - <i> optional</i>
                  </Box>
                }
              >
                <ControlledSelect
                  name="priority"
                  control={formMethods.control}
                  options={uiContent.priorityOptions}
                  placeholder="Choose an option"
                  filteringType="auto"
                  onChange={(e) => {
                    if (e.detail.selectedOption.value === "\u00A0") {
                      formMethods.setValue("priority", null);
                    }
                  }}
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Effort (hours) - <i> optional</i>
                  </Box>
                }
                errorText={get(formMethods.formState?.errors, `effort.message`)}
              >
                <ControlledInput
                  name="effort"
                  control={formMethods.control}
                  placeholder="Enter effort"
                  type="number"
                  inputMode="decimal"
                />
              </FormField>
              <FormField
                label={
                  <Box fontWeight="bold">
                    Description - <i> optional</i>
                  </Box>
                }
                errorText={get(
                  formMethods.formState?.errors,
                  `description.message`
                )}
              >
                <ControlledTextarea
                  name="description"
                  control={formMethods.control}
                  placeholder="Description"
                />
              </FormField>
              <FormField
                errorText={get(
                  formMethods.formState?.errors,
                  `isMilestone.message`
                )}
              >
                <ControlledCheckbox
                  name="milestone"
                  control={formMethods.control}
                >
                  Mark as milestone
                </ControlledCheckbox>
              </FormField>
            </ColumnLayout>
            <Box className={styles.modalFooter}>
              <SpaceBetween direction="horizontal" size="xs">
                <Button formAction="none" onClick={onCancelAdd} variant="link">
                  Cancel
                </Button>
                <Button variant="primary" disabled={disableAdd}>
                  Add
                </Button>
              </SpaceBetween>
            </Box>
          </Form>
        </form>
      </FormProvider>
    </div>
  );
};

export default CreateTaskForm;
